import type { RackNode, RowConfig } from '../store/editorStore.ts'
import { boxesIntersectOBB, type Box2D } from './collisionGuard.logic.ts'
import { rotatedRectBounds } from './floorPlan.logic.ts'

type FootprintField = 'widthM' | 'depthM'
type Bounds = { originXMm: number; originYMm: number; widthMm: number; heightMm: number }
type Options = { row: RowConfig; rack: RackNode; rowRacks: RackNode[]; allRacks: RackNode[]; obstacles?: Box2D[]; field: FootprintField; value: number; floorPlan: Bounds }

function toBox(rack: RackNode): Box2D {
  return { id: rack.id, x: rack.x, y: rack.y, w: rack.width, h: rack.height, rotation: rack.rotation }
}

export function previewRowFootprint(options: Options) {
  const { row, rack, rowRacks, allRacks, field, value } = options
  const errors: string[] = []
  const oldValue = rack[field] ?? (field === 'widthM' ? rack.width / 100 : rack.height / 100)
  if (!Number.isFinite(value) || value <= 0) errors.push('Размер footprint должен быть положительным числом')
  if (!rack.rowId || rack.rowId !== row.id) errors.push('Стойка не принадлежит выбранному ряду')
  if (rowRacks.some(candidate => candidate.widthM == null || candidate.depthM == null)) {
    errors.push('В ряду есть неизвестное основание. Сначала подтвердите ширину и глубину; автоматический сдвиг по оценочному контуру недоступен.')
  }
  const positions = rowRacks.map(candidate => candidate.rackPosInRow)
  if (!rowRacks.some(candidate => candidate.id === rack.id) || positions.some(position => !Number.isInteger(position) || (position ?? 0) < 1) || new Set(positions).size !== positions.length) {
    errors.push('Для изменения размеров нужны подтверждённые уникальные места всех объектов ряда')
  }
  const targetPosition = rack.rackPosInRow ?? 1
  const sequenceSign = row.sequenceDirection === 'reverse' ? -1 : 1
  const axis = row.axis ?? (row.orientation === 90 || row.orientation === 270 ? 'vertical' : 'horizontal')
  const deltaPx = (value - oldValue) * 100
  const radians = rack.rotation * Math.PI / 180
  const widthDelta = field === 'widthM' ? deltaPx : 0
  const axisDelta = axis === 'horizontal'
    ? Math.abs(Math.cos(radians)) * widthDelta
    : Math.abs(Math.sin(radians)) * widthDelta
  // Rotation is around the cabinet centre; keep the leading physical edge fixed.
  const x = rack.x - widthDelta / 2 + (axis === 'horizontal' ? sequenceSign * axisDelta / 2 : 0)
  const y = rack.y - (field === 'depthM' ? deltaPx / 2 : 0) + (axis === 'vertical' ? sequenceSign * axisDelta / 2 : 0)
  const targetPatch: Partial<RackNode> = {
    [field]: value,
    ...(field === 'widthM' ? { width: Math.round(value * 100) } : { height: Math.round(value * 100) }),
    x, y,
  }
  const rackUpdates: Array<{ id: string; patch: Partial<RackNode> }> = []
  rowRacks.filter(candidate => (candidate.rackPosInRow ?? 0) > targetPosition).forEach(candidate => {
    if (Math.abs(axisDelta) < 1e-8) return
    const patch = axis === 'horizontal'
      ? { x: candidate.x + sequenceSign * axisDelta }
      : { y: candidate.y + sequenceSign * axisDelta }
    rackUpdates.push({ id: candidate.id, patch: { ...patch, isDirty: true } })
  })
  if (errors.length) return { canApply: false, targetPatch: {} as Partial<RackNode>, rackUpdates: [], nextRow: row, errors }

  const proposed = rowRacks.map(candidate => {
    const update = candidate.id === rack.id ? targetPatch : rackUpdates.find(item => item.id === candidate.id)?.patch
    return { ...candidate, ...update, width: update?.width ?? candidate.width, height: update?.height ?? candidate.height }
  })
  const external = allRacks.filter(candidate => candidate.rowId !== row.id)
  const boxes = [...proposed.map(toBox), ...external.map(toBox), ...(options.obstacles ?? [])]
  const affectedIds = new Set([rack.id, ...rackUpdates.map(update => update.id)])
  for (let index = 0; index < proposed.length; index += 1) {
    const box = boxes[index]
    if (!affectedIds.has(box.id)) continue
    const minX = options.floorPlan.originXMm / 1000 * 100
    const minY = options.floorPlan.originYMm / 1000 * 100
    const maxX = minX + options.floorPlan.widthMm / 1000 * 100
    const maxY = minY + options.floorPlan.heightMm / 1000 * 100
    const bounds = rotatedRectBounds({ x: box.x, y: box.y, w: box.w, h: box.h, rotation: box.rotation ?? 0 })
    if (bounds.minX < minX || bounds.minY < minY || bounds.maxX > maxX || bounds.maxY > maxY) errors.push(`Стойка ${proposed[index].label} выходит за границы плана`)
    for (let otherIndex = 0; otherIndex < boxes.length; otherIndex += 1) {
      if (otherIndex !== index && boxesIntersectOBB(box, boxes[otherIndex])) errors.push(`Изменение размеров создаёт коллизию: ${proposed[index].label}`)
    }
  }
  const lastPosition = Math.max(...rowRacks.map(item => item.rackPosInRow ?? 0), ...(row.positions ?? []).map(item => item.position))
  const moveAnchor = (point: RowConfig['head'], tail: boolean) => {
    if (!point) return point
    const atTarget = Math.abs(point.xM * 100 - rack.x) < 1e-6 && Math.abs(point.yM * 100 - rack.y) < 1e-6
    const dx = atTarget ? x - rack.x : tail && lastPosition > targetPosition && axis === 'horizontal' ? sequenceSign * axisDelta : 0
    const dy = atTarget ? y - rack.y : tail && lastPosition > targetPosition && axis === 'vertical' ? sequenceSign * axisDelta : 0
    return { xM: Math.round((point.xM + dx / 100) * 1000) / 1000, yM: Math.round((point.yM + dy / 100) * 1000) / 1000 }
  }
  const nextRow = { ...row, head: moveAnchor(row.head, false), tail: moveAnchor(row.tail, true) }
  return { canApply: errors.length === 0, targetPatch, rackUpdates, nextRow, errors: [...new Set(errors)] }
}
